// Shared URL-parameter contract for Reporting → List drill-downs.
//
// Both Reporting pages (Briefs + Suppliers) navigate to the corresponding
// List page with filter state encoded in the URL. The List page reads
// those params on mount and seeds local filter state, so a drill is:
//
//   (1) shareable / bookmarkable
//   (2) refresh-safe (no reliance on location.state)
//   (3) reversible — the ?ref=reporting flag drives the Back-to-Reporting
//       affordance on the List page
//
// The build helpers below take a "drill intent" (high-level keys) and
// emit URLSearchParams with the canonical naming. List pages should use
// the read helpers below to translate params back into their local
// filter state — never read the params directly so the contract stays
// in one place.

// ============================================================================
// BUILDERS — call from Reporting components when wiring a drill click
// ============================================================================

// Builds a /briefs URL with the given drill filters applied. Always adds
// ref=reporting so the destination knows to show the Back link. Pass
// raw values (status as array or string, supplier ids as uuids); the
// builder handles encoding.
export function briefsListUrl(filters = {}) {
  const p = new URLSearchParams();
  if (filters.status) {
    const list = Array.isArray(filters.status) ? filters.status : [filters.status];
    if (list.length > 0) p.set('status', list.join(','));
  }
  if (filters.since)       p.set('since',        filters.since);
  if (filters.deadline)    p.set('deadline',     filters.deadline);
  if (filters.responses)   p.set('responses',    String(filters.responses));
  if (filters.respondedBy) p.set('responded_by', filters.respondedBy);
  if (filters.awardedTo)   p.set('awarded_to',   filters.awardedTo);
  if (filters.awardedWeek) p.set('awarded_week', filters.awardedWeek);
  if (filters.awardState)  p.set('award_state',  filters.awardState);
  if (filters.category)    p.set('category',     filters.category);
  if (filters.owner)       p.set('owner',        filters.owner);
  if (filters.sort)        p.set('sort',         filters.sort);
  if (filters.banner)      p.set('banner',       filters.banner);
  if (filters.bannerDescriptor) p.set('banner_descriptor', filters.bannerDescriptor);
  if (filters.bannerTone)       p.set('banner_tone',       filters.bannerTone);
  p.set('ref', 'reporting');
  return `/briefs?${p.toString()}`;
}

// Builds a /suppliers URL with the given drill filters applied. Same
// shape as briefsListUrl. Supplier-list-specific keys live here so
// Reporting components don't repeat the encoding.
export function suppliersListUrl(filters = {}) {
  const p = new URLSearchParams();
  if (filters.opStatus)            p.set('op_status',           filters.opStatus);
  if (filters.tier != null)        p.set('tier',                String(filters.tier));
  if (filters.category)            p.set('category',            filters.category);
  if (filters.region)              p.set('region',              filters.region);
  if (filters.compliance)          p.set('compliance',          filters.compliance);
  if (filters.dormant)             p.set('dormant',             filters.dormant);
  if (filters.createdWithin)       p.set('created_within',      filters.createdWithin);
  if (filters.invitedIn)           p.set('invited_in',          filters.invitedIn);
  if (filters.briefsInvitedIn90d != null) p.set('briefs_invited_in_90d', String(filters.briefsInvitedIn90d));
  if (filters.sort)                p.set('sort',                filters.sort);
  if (filters.banner)              p.set('banner',              filters.banner);
  if (filters.bannerDescriptor)    p.set('banner_descriptor',   filters.bannerDescriptor);
  if (filters.bannerTone)          p.set('banner_tone',         filters.bannerTone);
  p.set('ref', 'reporting');
  return `/suppliers?${p.toString()}`;
}

// ============================================================================
// READERS — call from List pages on mount to translate URL → local state
// ============================================================================

// Reads briefs-list drill params. Returns null for absent keys; callers
// destructure and apply to whatever local filter shape they have. The
// shape mirrors briefsListUrl's input.
export function parseBriefsDrillParams(searchParams) {
  if (!searchParams) return {};
  return {
    status: searchParams.get('status')?.split(',').filter(Boolean) || null,
    since: searchParams.get('since') || null,
    deadline: searchParams.get('deadline') || null,
    responses: searchParams.get('responses') || null,
    respondedBy: searchParams.get('responded_by') || null,
    awardedTo: searchParams.get('awarded_to') || null,
    awardedWeek: searchParams.get('awarded_week') || null,
    awardState: searchParams.get('award_state') || null,
    category: searchParams.get('category') || null,
    owner: searchParams.get('owner') || null,
    sort: searchParams.get('sort') || null,
    banner: searchParams.get('banner') || null,
    bannerDescriptor: searchParams.get('banner_descriptor') || null,
    bannerTone: searchParams.get('banner_tone') || null,
    fromReporting: searchParams.get('ref') === 'reporting',
  };
}

// Same shape for suppliers-list params.
export function parseSuppliersDrillParams(searchParams) {
  if (!searchParams) return {};
  return {
    opStatus: searchParams.get('op_status') || null,
    tier: searchParams.get('tier') ? Number(searchParams.get('tier')) : null,
    category: searchParams.get('category') || null,
    region: searchParams.get('region') || null,
    compliance: searchParams.get('compliance') || null,
    dormant: searchParams.get('dormant') || null,
    createdWithin: searchParams.get('created_within') || null,
    invitedIn: searchParams.get('invited_in') || null,
    briefsInvitedIn90d: searchParams.get('briefs_invited_in_90d'),
    sort: searchParams.get('sort') || null,
    banner: searchParams.get('banner') || null,
    bannerDescriptor: searchParams.get('banner_descriptor') || null,
    bannerTone: searchParams.get('banner_tone') || null,
    fromReporting: searchParams.get('ref') === 'reporting',
  };
}

// True when *any* param indicates a drill landed on this page — used to
// decide whether to show the drill banner / back link.
export function hasAnyDrillParam(searchParams) {
  if (!searchParams) return false;
  if (searchParams.get('ref') === 'reporting') return true;
  for (const key of [
    'status','since','deadline','responses','responded_by','awarded_to',
    'awarded_week','award_state','category','owner','sort','banner',
    'op_status','tier','region','compliance','dormant','created_within',
    'invited_in','briefs_invited_in_90d',
  ]) {
    if (searchParams.get(key) != null) return true;
  }
  return false;
}

// ============================================================================
// SUPPLIER OPERATIONAL STATUS — sythesised from multiple columns
// ============================================================================

// The brief models op_status as an enum (active|preferred|on_hold|do_not_use
// |pending_setup) but the database keeps these as discrete columns. This
// pair of helpers translates between the URL representation and the
// row-level check so consumers stay decoupled from the column layout.
//
// Order matters: a row qualifies as the FIRST status that matches, so
// e.g. a preferred + on-hold supplier reads as on_hold (which is the
// blocking signal). Mirror this order client-side for consistency.

export function supplierOpStatus(supplier) {
  if (!supplier) return null;
  if (supplier.finance_approved === null || supplier.finance_approved === undefined) return 'pending_setup';
  if (supplier.do_not_use_status === 'Do Not Use') return 'do_not_use';
  if (supplier.on_hold === true) return 'on_hold';
  if (supplier.preferred_supplier === 'Preferred Supplier') return 'preferred';
  if (supplier.status === 'active') return 'active';
  return null;
}

export function matchesOpStatus(supplier, opStatus) {
  if (!opStatus) return true;
  return supplierOpStatus(supplier) === opStatus;
}

// Human-readable banner copy for the drill-banner row. Reporting
// components can override via the optional `banner` URL param, but this
// gives every drill a sensible default so the banner reads naturally.
export const BRIEFS_BANNER_DEFAULTS = {
  needs_attention:  { tone: 'red',    descriptor: 'needing attention (past deadline, no responses)' },
  awarded:          { tone: 'green',  descriptor: 'awarded in this period' },
  live:             { tone: 'teal',   descriptor: 'currently live (draft / sent / responses in)' },
  drafted:          { tone: 'grey',   descriptor: 'in draft' },
};
export const SUPPLIERS_BANNER_DEFAULTS = {
  pending_setup:    { tone: 'teal',   descriptor: 'awaiting Fusion account set-up' },
  dormant:          { tone: 'amber',  descriptor: 'with no activity in the past 90 days' },
  under_utilised:   { tone: 'amber',  descriptor: 'active but never invited to a brief in 90 days' },
  preferred:        { tone: 'green',  descriptor: 'flagged as Preferred' },
};
